import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO, { serviceSchema } from '../../components/SEO';
import {
  ArrowRight,
  Sparkles,
  Trophy,
  Flame,
  Heart,
  Share2,
  QrCode,
  Ticket,
  Zap,
  ScanFace,
  Star,
  Award,
  Bike,
  Camera,
  Waves,
  Mountain,
  Timer,
  Snowflake,
  Instagram,
  MessageCircle,
  Send,
  Music2,
  ChevronRight,
  Medal,
  Radio,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import StatsDashboard from '../../components/StatsDashboard';
import { phoneMockups, sportsImages } from '../../mock';
import { getLangFromPath, localizePath } from '../../i18n';

/* Phone frame */
function PhoneFrame({ children }) {
  return (
    <div className="relative mx-auto w-[220px] md:w-[240px]">
      <div className="relative aspect-[9/19] rounded-[2.5rem] bg-gradient-to-b from-[#1a1a24] to-[#0a0a0f] p-2 border border-white/10 shadow-2xl shadow-black/70">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-2xl z-30" />
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-[#0a0a0f]">{children}</div>
      </div>
      <div className="absolute -inset-6 -z-10 bg-emerald-500/12 blur-3xl rounded-full" />
    </div>
  );
}

/* Screen 1 : Race selfie search */
function ScreenRace() {
  const { t } = useTranslation();
  return (
    <>
      <img src={sportsImages.finish} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/40" />

      <div className="absolute top-10 left-3 right-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-[10px] font-black shadow">
          42K
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold text-white leading-tight">{t('mockups.sports.eventTitle')}</div>
          <div className="text-[8px] text-white/70 leading-tight">{t('mockups.sports.runnersMeta')}</div>
        </div>
      </div>

      <div className="absolute top-[34%] left-1/2 -translate-x-1/2 w-[52%] aspect-square">
        <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white rounded-tl-md" />
        <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white rounded-tr-md" />
        <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white rounded-bl-md" />
        <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white rounded-br-md" />
      </div>

      <div className="absolute bottom-6 left-3 right-3 text-center">
        <div className="text-[10px] font-semibold text-white mb-1">{t('mockups.sports.lookingForPhotos')}</div>
        <div className="text-[8px] text-white/70 mb-2">{t('mockups.sports.selfieOrBib')}</div>
        <div className="w-11 h-11 rounded-full bg-white mx-auto" />
      </div>
    </>
  );
}

/* Screen 2 : Race album with sponsor + time */
function ScreenRaceAlbum() {
  const { t } = useTranslation();
  return (
    <>
      <div className="absolute inset-0 bg-[#0a0a0f]" />

      <div className="absolute top-10 left-0 right-0 px-3">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-[9px] font-black">42K</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-white leading-tight">{t('mockups.sports.raceTag')}</div>
            <div className="text-[8px] text-white/50 leading-tight">{t('mockups.sports.bib')}</div>
          </div>
          <Medal className="w-4 h-4 text-amber-400" />
        </div>

        {/* Result banner */}
        <div className="rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 p-1.5 flex items-center justify-between text-white">
          <div className="flex items-center gap-1.5">
            <Timer className="w-3 h-3" />
            <span className="text-[9px] font-bold">{t('mockups.sports.time')}</span>
            <span className="text-[11px] font-black">3:47:22</span>
          </div>
          <div className="text-[7px] uppercase tracking-wider opacity-80">{t('mockups.sports.position')}</div>
          <div className="text-[9px] font-black">PB</div>
        </div>
      </div>

      <div className="absolute top-[36%] left-3 right-3">
        <div className="rounded-lg overflow-hidden aspect-[4/3] border border-white/8 relative">
          <img src={sportsImages.finish} alt="" className="w-full h-full object-cover" />
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-[7px] font-black italic">Imperial</div>
        </div>
      </div>

      <div className="absolute top-[63%] left-3 right-3">
        <div className="grid grid-cols-4 gap-1 mb-2">
          {[sportsImages.marathon, sportsImages.trail, sportsImages.cycling, sportsImages.celebrate].map((s, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden">
              <img src={s} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="text-[7px] uppercase tracking-wider text-white/40 mb-1">{t('mockups.sports.raceSponsors')}</div>
        <div className="flex items-center gap-1">
          {[
            { name: 'Adidas', bg: 'bg-black border border-white/30' },
            { name: 'Powerade', bg: 'bg-blue-600' },
            { name: 'Garmin', bg: 'bg-slate-700' },
          ].map((s) => (
            <div key={s.name} className={`flex-1 h-5 rounded ${s.bg} flex items-center justify-center`}>
              <span className="text-[7px] font-bold text-white italic">{s.name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* Screen 3 : Share PR */
function ScreenSharePR() {
  const { t } = useTranslation();
  const contacts = [
    { name: 'Coach', img: sportsImages.marathon, social: 'wa' },
    { name: 'Nico', img: sportsImages.trail, social: 'ig' },
    { name: 'Team', img: sportsImages.cycling, social: 'tt' },
    { name: 'Pau', img: sportsImages.celebrate, social: 'wa' },
  ];
  const iconOf = {
    wa: { Icon: MessageCircle, bg: 'bg-emerald-500' },
    ig: { Icon: Instagram, bg: 'bg-gradient-to-br from-fuchsia-500 to-orange-500' },
    tt: { Icon: Music2, bg: 'bg-black border border-white/40' },
  };
  return (
    <>
      <img src={sportsImages.celebrate} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/50" />

      <div className="absolute top-12 left-3 right-3 flex items-center justify-between">
        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-400 text-slate-900 text-[9px] font-black">
          <Medal className="w-3 h-3" /> PB · 3:47:22
        </div>
        <div className="text-[8px] text-white/80">{t('mockups.sports.myRace')}</div>
      </div>

      <div className="absolute left-3 right-3 top-[42%] rounded-2xl bg-white/95 backdrop-blur-md p-3 shadow-xl">
        <div className="text-[8px] uppercase tracking-wider text-black/50 mb-2 font-semibold">{t('mockups.common.shareWith')}</div>
        <div className="flex items-center justify-between gap-1">
          {contacts.map((c) => {
            const { Icon, bg } = iconOf[c.social];
            return (
              <div key={c.name} className="flex flex-col items-center gap-1">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-black/10">
                    <img src={c.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${bg} flex items-center justify-center shadow`}>
                    <Icon className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
                <div className="text-[8px] text-black font-medium">{c.name}</div>
              </div>
            );
          })}
          <div className="flex flex-col items-center gap-1">
            <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center">
              <ChevronRight className="w-4 h-4 text-black" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center gap-2">
        <span className="text-white text-[9px] font-black italic">#MaratónBA</span>
        <span className="text-white/60 text-[8px]">·</span>
        <span className="text-white text-[9px] font-black italic">Adidas</span>
      </div>
    </>
  );
}

/* Race Event Card */
function RaceEventCard() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full max-w-[440px] mx-auto">
      <div className="relative w-full aspect-[3/4] rounded-3xl overflow-hidden border border-white/10">
        <img src={sportsImages.finish} alt="race" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

        <div className="absolute top-6 left-1/2 -translate-x-1/2 w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-display font-black text-2xl shadow-2xl shadow-emerald-500/40 border-4 border-[#0a0a0f]">
          m
        </div>

        <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-[#0a0a0f]/85 backdrop-blur-md border border-white/10 overflow-hidden">
          <div className="relative h-24 overflow-hidden bg-[#111116]">
            <img src={sportsImages.marathon} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-transparent to-[#0a0a0f]" />
            <div className="relative h-full flex items-center justify-center gap-6 px-4">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center text-white text-2xl shadow-lg">
                  🏃
                </div>
                <div className="text-[9px] font-black text-emerald-300 mt-1 tracking-wider">42K</div>
              </div>
              <div className="text-white/70 font-display font-bold text-lg">·</div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 text-2xl shadow-lg">
                  🏁
                </div>
                <div className="text-[9px] font-black text-white mt-1 tracking-wider">FINISH</div>
              </div>
            </div>
          </div>

          <div className="p-4 pt-3">
            <div className="text-sm font-display font-bold text-white leading-tight">{t('mockups.sports.eventTitle')}</div>
            <div className="text-xs text-white/60 mt-0.5">{t('mockups.sports.eventLoc')}</div>
            <div className="text-xs text-white/40 mt-1">{t('mockups.sports.eventDate')}</div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
              <div className="text-[11px] text-emerald-300 font-semibold flex items-center gap-1">
                <Camera className="w-3 h-3" /> {t('mockups.sports.photosCount')}
              </div>
              <div className="px-2.5 py-1 rounded-full bg-white text-slate-900 text-[10px] font-bold flex items-center gap-1">
                {t('mockups.sports.findMyPhotos')} <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Sports gallery — many sports one passion */
function SportsGallery() {
  const items = [
    { src: sportsImages.marathon, name: 'Marathon' },
    { src: sportsImages.mtb, name: 'MTB' },
    { src: sportsImages.ski, name: 'Cross-country ski' },
    { src: sportsImages.trailMountain, name: 'Trail' },
    { src: sportsImages.triathlon, name: 'Triathlon' },
    { src: sportsImages.cycling, name: 'Cycling' },
    { src: sportsImages.obstacle, name: 'Obstacle Race' },
    { src: sportsImages.mtbSplash, name: 'Enduro' },
    { src: sportsImages.trail, name: 'Ultra Trail' },
  ];
  return (
    <div className="overflow-hidden">
      <div className="flex gap-3 marquee-track">
        {[...items, ...items].map((s, i) => (
          <div key={i} className="relative w-[240px] h-[300px] shrink-0 rounded-2xl overflow-hidden border border-white/10">
            <img src={s.src} alt={s.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3 text-white font-display text-xl font-bold">{s.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Sports sponsor tiers */
function SportsSponsorTiers() {
  const { t } = useTranslation();
  const tiers = [
    { name: t('mockups.sports.tierPlatinum'), badge: 'from-slate-100 to-slate-300 text-slate-900', logos: ['ADIDAS'], size: 'h-16' },
    { name: t('mockups.sports.tierGold'), badge: 'from-amber-300 to-amber-500 text-amber-900', logos: ['Garmin', 'Powerade'], size: 'h-12' },
    { name: t('mockups.sports.tierSilver'), badge: 'from-zinc-300 to-zinc-400 text-zinc-900', logos: ['Gatorade', 'Salomon', 'Suunto'], size: 'h-10' },
    { name: t('mockups.sports.tierBronze'), badge: 'from-amber-700 to-amber-900 text-amber-50', logos: ['New Balance', 'Buff', 'Enervit', 'Compressport'], size: 'h-8' },
  ];
  return (
    <div className="space-y-4">
      {tiers.map((t) => (
        <div key={t.name} className="flex items-center gap-4">
          <div className="w-20 shrink-0 text-right">
            <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${t.badge} text-[10px] font-black uppercase tracking-wider`}>{t.name}</div>
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            {t.logos.map((l) => (
              <div key={l} className={`${t.size} px-4 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center min-w-[100px]`}>
                <span className="text-white/85 font-display font-bold text-sm">{l}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Sports email templates */
function SportsEmailMockup() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="rounded-2xl bg-white shadow-2xl p-4 max-w-[300px] relative z-10">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/8">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-black text-[10px]">42K</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-black leading-tight">{t('mockups.sports.emailTitleFinisher')}</div>
            <div className="text-[8px] text-black/50">{t('mockups.sports.emailToFinisher')}</div>
          </div>
        </div>
        <div className="text-[11px] font-black text-black leading-tight mb-2">{t('mockups.sports.emailSubjectFinisher')}</div>
        <div className="w-full aspect-[16/10] rounded-lg overflow-hidden mb-2">
          <img src={sportsImages.finish} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 p-2.5 text-white">
          <div className="text-[8px] uppercase tracking-wider opacity-80">{t('mockups.common.sponsorCoupon')}</div>
          <div className="text-sm font-black leading-tight">{t('mockups.sports.couponBody')}</div>
          <div className="text-[9px] mt-1 opacity-90">{t('mockups.sports.couponHint')}</div>
        </div>
      </div>

      <div className="rounded-2xl bg-white shadow-2xl p-4 max-w-[280px] absolute top-16 right-0 rotate-3 z-20">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-black/8">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white">
            <Trophy className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-black leading-tight">{t('mockups.sports.emailTitleCoach')}</div>
            <div className="text-[8px] text-black/50">{t('mockups.sports.emailToCoach')}</div>
          </div>
        </div>
        <div className="text-[11px] font-black text-black leading-tight mb-2">{t('mockups.sports.coachSubject')}</div>
        <div className="text-[9px] text-black/70 leading-relaxed mb-2">
          {t('mockups.sports.coachBodyPrefix')} <span className="font-bold text-emerald-600">-8'32''</span> {t('mockups.sports.coachBodyMid')}
        </div>
        <div className="grid grid-cols-3 gap-1 mb-2">
          {[sportsImages.marathon, sportsImages.finish, sportsImages.celebrate].map((s, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden">
              <img src={s} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="h-6 rounded bg-emerald-500 flex items-center justify-center text-white text-[9px] font-black">
          {t('mockups.common.viewMyAlbum')}
        </div>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-2xl p-4 max-w-[260px] absolute bottom-0 left-8 -rotate-2 z-0">
        <div className="text-[9px] uppercase tracking-wider opacity-80 mb-1">{t('mockups.sports.nextRace')}</div>
        <div className="text-sm font-black leading-tight mb-2">{t('mockups.sports.ironmanTitle')}</div>
        <div className="text-[9px] opacity-90 leading-relaxed mb-3">
          {t('mockups.sports.ironmanBody')}
        </div>
        <div className="h-6 rounded bg-white text-emerald-700 flex items-center justify-center text-[9px] font-bold">
          {t('mockups.sports.registerCta')}
        </div>
      </div>
    </div>
  );
}

const sportKinds = [
  { icon: Trophy, name: 'Marathon', desc: '42K, 21K, 10K y ultra' },
  { icon: Waves, name: 'Triathlon / Ironman', desc: 'Sprint, Olímpico, Half y Full' },
  { icon: Bike, name: 'Cycling / MTB', desc: 'Ruta, gravel, XC y enduro' },
  { icon: Mountain, name: 'Trail / Ultra', desc: 'Skyrace, ultra y trekking' },
  { icon: Star, name: 'Torneos & Ligas', desc: 'Fútbol, tenis, pádel, básquet, hockey' },
  { icon: Flame, name: 'OCR & CrossFit', desc: 'Spartan, Tough Mudder, box games' },
  { icon: Snowflake, name: 'Ski / Nieve', desc: 'Cross-country, ski mountaineering' },
  { icon: Award, name: 'Motor / eSports', desc: 'Rally, karting, MotoGP, gaming' },
];

export default function Sports() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);
  const howSteps = t('services.sports.howItWorks.steps', { returnObjects: true }) || [];
  const sponsorItems = t('services.sports.sponsors.items', { returnObjects: true }) || [];
  const emailItems = t('services.sports.emails.items', { returnObjects: true }) || [];
  const measureTags = t('services.sports.measure.tags', { returnObjects: true }) || [];
  const impactItems = t('services.sports.impact.items', { returnObjects: true }) || [];
  return (
    <>
      <SEO
        title={t('services.sports.seoTitle')}
        description={t('services.sports.seoDescription')}
        path="/servicios/deportes"
        schema={serviceSchema({ name: 'Fotos con IA para Eventos Deportivos', description: 'Reconocimiento facial + venta de fotos para carreras, maratones, triatlones, torneos amateurs y ligas deportivas.', path: '/servicios/deportes' })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-20 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-32 w-[500px] h-[500px] bg-emerald-500/12 rounded-full blur-[140px]" />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-violet-500/12 rounded-full blur-[160px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Link to={to('/')} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-300 mb-5 hover:text-emerald-200 transition-colors">
                <span>{t('services.sports.breadcrumb')}</span><span>/</span><span className="text-white/60">{t('services.sports.breadcrumbCurrent')}</span>
              </Link>
              <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02]">
                {t('services.sports.heroTitle1')}
                <span className="block bg-gradient-to-r from-emerald-300 via-teal-300 to-violet-300 bg-clip-text text-transparent">
                  {t('services.sports.heroTitleHighlight')}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                {t('services.sports.heroSubtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to={to('/request-demo')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold">
                    {t('services.sports.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/precios')}>
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.sports.ctaPricing')}
                  </Button>
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                <div>
                  <div className="font-display text-2xl font-bold text-white">5x</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.sports.heroStats.sales')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">99.2%</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.sports.heroStats.precision')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">&lt;3s</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.sports.heroStats.delivery')}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <RaceEventCard />
            </div>
          </div>
        </div>
      </section>

      {/* MANY SPORTS ONE PASSION */}
      <section className="py-16 lg:py-20 bg-[#08080c] border-y border-white/5">
        <div className="container-mi mb-8 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 mb-3">{t('services.sports.gallery.eyebrow')}</div>
          <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight">
            {t('services.sports.gallery.titleLine1')} <span className="bg-gradient-to-r from-emerald-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.sports.gallery.titleHighlight')}</span>
          </h2>
        </div>
        <SportsGallery />
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 mb-3">{t('services.sports.howItWorks.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.sports.howItWorks.title')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: 1, Screen: ScreenRace, color: 'bg-emerald-500' },
              { n: 2, Screen: ScreenRaceAlbum, color: 'bg-teal-500' },
              { n: 3, Screen: ScreenSharePR, color: 'bg-amber-500' },
            ].map((s, i) => (
              <div key={s.n} className="flex flex-col items-center text-center">
                <PhoneFrame>
                  <s.Screen />
                </PhoneFrame>
                <div className={`mt-6 w-8 h-8 rounded-full ${s.color} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                  {s.n}
                </div>
                <h3 className="font-display text-lg md:text-xl font-semibold mt-4 mb-2">{howSteps[i]?.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed max-w-xs">{howSteps[i]?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSOR ECOSYSTEM */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 mb-3 flex items-center gap-2">
                <Award className="w-3.5 h-3.5" /> {t('services.sports.sponsors.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.sports.sponsors.titleLine1')} <span className="bg-gradient-to-r from-emerald-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.sports.sponsors.titleHighlight')}</span>
                <br />
                {t('services.sports.sponsors.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.sports.sponsors.description')}
              </p>
              <ul className="mt-8 space-y-3">
                {[Sparkles, Ticket, QrCode, Radio].map((Icon, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-400/25 flex items-center justify-center text-emerald-300 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm leading-relaxed pt-1">{sponsorItems[i]}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-[#0d0d13] border border-white/8 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="text-sm font-semibold text-white">{t('services.sports.sponsors.dashboardTitle')}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">{t('services.sports.sponsors.dashboardActive')}</div>
              </div>
              <SportsSponsorTiers />
              <div className="mt-6 pt-6 border-t border-white/8 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="font-display text-xl font-bold text-white">3.8M</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.sports.sponsors.dashboardImpressions')}</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-white">142k</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.sports.sponsors.dashboardClicks')}</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-emerald-300">5.2x ROI</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.sports.sponsors.dashboardReturn')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EMAILS A TU MEDIDA */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 -translate-y-1/2 -left-40 w-[500px] h-[500px] bg-emerald-500/6 rounded-full blur-[140px]" />
          <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[500px] h-[500px] bg-amber-500/6 rounded-full blur-[140px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 mb-3 flex items-center gap-2">
                <ScanFace className="w-3.5 h-3.5" /> {t('services.sports.emails.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.sports.emails.titleLine1')}
                <br />
                {t('services.sports.emails.titleLine2Prefix')} <span className="bg-gradient-to-r from-emerald-300 to-amber-300 bg-clip-text text-transparent">{t('services.sports.emails.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.sports.emails.description')}
              </p>
              <div className="mt-8 space-y-3">
                {[Ticket, Heart, QrCode, Trophy, Sparkles].map((Icon, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#111116] border border-white/8">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-400/25 flex items-center justify-center text-emerald-300 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{emailItems[i]?.title}</div>
                      <div className="text-xs text-white/60 mt-0.5">{emailItems[i]?.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[520px]">
              <SportsEmailMockup />
            </div>
          </div>
        </div>
      </section>

      {/* SPORT KINDS */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 mb-3">{t('services.sports.sportKinds.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.sports.sportKinds.title')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {sportKinds.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.name} className="card-lift p-6 rounded-2xl bg-[#111116] border border-white/8">
                  <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-400/25 flex items-center justify-center text-emerald-300 mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display text-lg font-semibold text-white">{s.name}</div>
                  <div className="text-sm text-white/60 mt-1">{s.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEASURE SUCCESS — Statistics dashboard */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-teal-500/12 to-lime-500/15 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-emerald-500/20 rounded-full blur-[160px]" />
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> {t('services.sports.measure.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white">
                {t('services.sports.measure.titleLine1')}
                <br />
                {t('services.sports.measure.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
                {t('services.sports.measure.description')}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {measureTags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-sm text-white">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <StatsDashboard
                config={{
                  accent: {
                    chartFrom: 'from-emerald-400',
                    chartVia: 'via-teal-400',
                    chartTo: 'to-lime-400',
                    dayPillBg: 'bg-emerald-500',
                  },
                  browserUrl: 'dashboard.momentia.shop / Maratón BsAs 2026',
                  period: 'Día de carrera · en vivo',
                  kpis: [
                    { icon: '🏁', label: 'Finishers', value: '18.240', sub: '98% terminaron', color: 'text-emerald-600' },
                    { icon: '📸', label: 'Fotos por corredor', value: '8', sub: '42k entregadas', color: 'text-teal-600' },
                    { icon: '🔥', label: 'Engagement', value: '52%', sub: '24k downloads · 12k shares', color: 'text-amber-600' },
                  ],
                  chart: {
                    title: 'Cruces de meta por hora',
                    bars: [22, 45, 68, 92, 88, 76, 65, 48, 35, 26, 18, 12, 8],
                  },
                  sponsorRow: {
                    title: 'Reporte de sponsors',
                    subtitle: 'Adidas · Powerade · Garmin',
                    items: [
                      { icon: '👁', label: 'Impresiones', value: '3.8M' },
                      { icon: '🔗', label: 'Clicks a links', value: '142k' },
                      { icon: '🎟️', label: 'Cupones', value: '2.184' },
                    ],
                  },
                  badges: [
                    { emoji: '🏅', text: '+64 PB', pos: 'tl' },
                    { emoji: '🔥', text: '+182 shares', pos: 'br' },
                    { emoji: '💰', text: '+$8.4k ventas', pos: 'bml' },
                  ],
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT METRICS */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/15 via-teal-500/10 to-violet-500/15 pointer-events-none" />
        <div className="container-mi relative">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3">{t('services.sports.impact.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white leading-tight">
              {t('services.sports.impact.titleLine1')}
              <br />
              {t('services.sports.impact.titleLine2')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {impactItems.map((m) => (
              <div key={m.label} className="rounded-2xl bg-white/[0.08] backdrop-blur-md border border-white/20 p-5">
                <div className="font-display text-3xl md:text-4xl font-bold text-white">{m.value}</div>
                <div className="text-sm text-white/70 mt-2 leading-snug">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-[#0e2a1c] via-[#101018] to-[#0e0e14] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-emerald-500/15 rounded-full blur-[100px]" />
            <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-emerald-400/25 bg-emerald-500/10 text-xs text-emerald-200 mb-6">
                <Zap className="w-3 h-3" /> {t('services.sports.finalCta.badge')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white">
                {t('services.sports.finalCta.title')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('services.sports.finalCta.subtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to={to('/request-demo')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                    {t('services.sports.finalCta.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/precios')}>
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.sports.finalCta.ctaPricing')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
